import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert, IconButton,
  ToggleButton, ToggleButtonGroup, Tooltip, Divider, SwipeableDrawer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MapIcon from "@mui/icons-material/Map";
import ListIcon from "@mui/icons-material/List";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// ─── Animal SVG icons ─────────────────────────────────────────────────────────
function RaccoonSVG({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <ellipse cx="16" cy="17" rx="12" ry="11" fill="#a855f7"/>
      <ellipse cx="8" cy="9" rx="5" ry="6" fill="#a855f7"/>
      <ellipse cx="24" cy="9" rx="5" ry="6" fill="#a855f7"/>
      <ellipse cx="8" cy="8" rx="3" ry="4" fill="#4c1d95"/>
      <ellipse cx="24" cy="8" rx="3" ry="4" fill="#4c1d95"/>
      <ellipse cx="10" cy="19" rx="5" ry="4" fill="#7c3aed"/>
      <ellipse cx="22" cy="19" rx="5" ry="4" fill="#7c3aed"/>
      <ellipse cx="16" cy="20" rx="3" ry="2" fill="#f5f3ff"/>
      <ellipse cx="14" cy="22" rx="1.5" ry="1" fill="#4c1d95"/>
      <ellipse cx="18" cy="22" rx="1.5" ry="1" fill="#4c1d95"/>
      <ellipse cx="16" cy="23.5" rx="2" ry="1.2" fill="#a855f7"/>
      <ellipse cx="9" cy="17" rx="1.2" ry="1" fill="#4c1d95"/>
      <ellipse cx="23" cy="17" rx="1.2" ry="1" fill="#4c1d95"/>
    </svg>
  );
}

function CoyoteSVG({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <polygon points="8,14 5,2 12,9" fill="#d97706"/>
      <polygon points="24,14 27,2 20,9" fill="#d97706"/>
      <polygon points="8,12 6,4 11,9" fill="#fbbf24"/>
      <polygon points="24,12 26,4 21,9" fill="#fbbf24"/>
      <ellipse cx="16" cy="18" rx="11" ry="10" fill="#d97706"/>
      <ellipse cx="16" cy="23" rx="5" ry="4" fill="#b45309"/>
      <ellipse cx="16" cy="21.5" rx="2" ry="1.2" fill="#1c1917"/>
      <ellipse cx="12" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="20" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="12" cy="16" rx="1.1" ry="1.1" fill="#92400e"/>
      <ellipse cx="20" cy="16" rx="1.1" ry="1.1" fill="#92400e"/>
      <path d="M14 25 Q16 26.5 18 25" fill="none" stroke="#1c1917" strokeWidth="0.8"/>
    </svg>
  );
}

function OpossumSVG({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <ellipse cx="9" cy="9" rx="5" ry="5" fill="#94a3b8"/>
      <ellipse cx="23" cy="9" rx="5" ry="5" fill="#94a3b8"/>
      <ellipse cx="9" cy="9" rx="3" ry="3" fill="#e2e8f0"/>
      <ellipse cx="23" cy="9" rx="3" ry="3" fill="#e2e8f0"/>
      <ellipse cx="16" cy="18" rx="11" ry="10" fill="#e2e8f0"/>
      <ellipse cx="16" cy="24" rx="4" ry="5" fill="#cbd5e1"/>
      <ellipse cx="16" cy="22" rx="1.8" ry="1.2" fill="#f9a8d4"/>
      <ellipse cx="12" cy="16" rx="1.8" ry="1.8" fill="#1e293b"/>
      <ellipse cx="20" cy="16" rx="1.8" ry="1.8" fill="#1e293b"/>
      <ellipse cx="12.6" cy="15.4" rx="0.5" ry="0.5" fill="#f8fafc"/>
      <ellipse cx="20.6" cy="15.4" rx="0.5" ry="0.5" fill="#f8fafc"/>
      <circle cx="11" cy="22" r="0.5" fill="#94a3b8"/>
      <circle cx="13" cy="23" r="0.5" fill="#94a3b8"/>
      <circle cx="21" cy="22" r="0.5" fill="#94a3b8"/>
      <circle cx="19" cy="23" r="0.5" fill="#94a3b8"/>
    </svg>
  );
}

function GroundhogSVG({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <ellipse cx="10" cy="10" rx="4" ry="4" fill="#92400e"/>
      <ellipse cx="22" cy="10" rx="4" ry="4" fill="#92400e"/>
      <ellipse cx="10" cy="10" rx="2.5" ry="2.5" fill="#b45309"/>
      <ellipse cx="22" cy="10" rx="2.5" ry="2.5" fill="#b45309"/>
      <ellipse cx="16" cy="18" rx="12" ry="11" fill="#92400e"/>
      <ellipse cx="9" cy="20" rx="4" ry="3.5" fill="#b45309"/>
      <ellipse cx="23" cy="20" rx="4" ry="3.5" fill="#b45309"/>
      <ellipse cx="16" cy="22" rx="4" ry="3" fill="#b45309"/>
      <ellipse cx="16" cy="20.5" rx="1.5" ry="1" fill="#1c1917"/>
      <ellipse cx="12" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="20" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="12" cy="16" rx="1.2" ry="1.2" fill="#78350f"/>
      <ellipse cx="20" cy="16" rx="1.2" ry="1.2" fill="#78350f"/>
      <rect x="14.5" y="23.5" width="1.5" height="2" rx="0.4" fill="#fef9c3"/>
      <rect x="16.5" y="23.5" width="1.5" height="2" rx="0.4" fill="#fef9c3"/>
    </svg>
  );
}

function FoxSVG({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      <polygon points="9,15 4,1 14,10" fill="#ea580c"/>
      <polygon points="23,15 28,1 18,10" fill="#ea580c"/>
      <polygon points="9,13 6,4 13,10" fill="#fef3c7"/>
      <polygon points="23,13 26,4 19,10" fill="#fef3c7"/>
      <ellipse cx="16" cy="18" rx="11" ry="10" fill="#ea580c"/>
      <ellipse cx="16" cy="20" rx="6" ry="7" fill="#fef3c7"/>
      <ellipse cx="16" cy="24" rx="4" ry="3.5" fill="#fef3c7"/>
      <ellipse cx="16" cy="22.5" rx="1.8" ry="1.2" fill="#1c1917"/>
      <ellipse cx="11.5" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="20.5" cy="16" rx="2" ry="2" fill="#fef3c7"/>
      <ellipse cx="11.5" cy="16" rx="1.2" ry="1.2" fill="#c2410c"/>
      <ellipse cx="20.5" cy="16" rx="1.2" ry="1.2" fill="#c2410c"/>
      <ellipse cx="11.9" cy="15.6" rx="0.4" ry="0.4" fill="#fff"/>
      <ellipse cx="20.9" cy="15.6" rx="0.4" ry="0.4" fill="#fff"/>
      <path d="M14 25.5 Q16 27 18 25.5" fill="none" stroke="#1c1917" strokeWidth="0.8"/>
    </svg>
  );
}

// ─── Animal config ────────────────────────────────────────────────────────────
const ANIMALS = [
  { id: "raccoon",   label: "Raccoon",   taxonId: 41663, color: "#a855f7", Icon: RaccoonSVG },
  { id: "coyote",    label: "Coyote",    taxonId: 42051, color: "#d97706", Icon: CoyoteSVG },
  { id: "opossum",   label: "Opossum",   taxonId: 42652, color: "#94a3b8", Icon: OpossumSVG },
  { id: "groundhog", label: "Groundhog", taxonId: 46095, color: "#92400e", Icon: GroundhogSVG },
  { id: "fox",       label: "Fox",       taxonId: 42069, color: "#ea580c", Icon: FoxSVG },
];

// ─── SVG strings for Leaflet DivIcon markers ─────────────────────────────────
const ANIMAL_SVG_HTML = {
  raccoon: `<svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="17" rx="12" ry="11" fill="#a855f7"/>
    <ellipse cx="8" cy="9" rx="5" ry="6" fill="#a855f7"/>
    <ellipse cx="24" cy="9" rx="5" ry="6" fill="#a855f7"/>
    <ellipse cx="8" cy="8" rx="3" ry="4" fill="#4c1d95"/>
    <ellipse cx="24" cy="8" rx="3" ry="4" fill="#4c1d95"/>
    <ellipse cx="10" cy="19" rx="5" ry="4" fill="#7c3aed"/>
    <ellipse cx="22" cy="19" rx="5" ry="4" fill="#7c3aed"/>
    <ellipse cx="16" cy="20" rx="3" ry="2" fill="#f5f3ff"/>
    <ellipse cx="14" cy="22" rx="1.5" ry="1" fill="#4c1d95"/>
    <ellipse cx="18" cy="22" rx="1.5" ry="1" fill="#4c1d95"/>
    <ellipse cx="16" cy="23.5" rx="2" ry="1.2" fill="#a855f7"/>
    <ellipse cx="9" cy="17" rx="1.2" ry="1" fill="#4c1d95"/>
    <ellipse cx="23" cy="17" rx="1.2" ry="1" fill="#4c1d95"/>
  </svg>`,
  coyote: `<svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <polygon points="8,14 5,2 12,9" fill="#d97706"/>
    <polygon points="24,14 27,2 20,9" fill="#d97706"/>
    <polygon points="8,12 6,4 11,9" fill="#fbbf24"/>
    <polygon points="24,12 26,4 21,9" fill="#fbbf24"/>
    <ellipse cx="16" cy="18" rx="11" ry="10" fill="#d97706"/>
    <ellipse cx="16" cy="23" rx="5" ry="4" fill="#b45309"/>
    <ellipse cx="16" cy="21.5" rx="2" ry="1.2" fill="#1c1917"/>
    <ellipse cx="12" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="20" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="12" cy="16" rx="1.1" ry="1.1" fill="#92400e"/>
    <ellipse cx="20" cy="16" rx="1.1" ry="1.1" fill="#92400e"/>
  </svg>`,
  opossum: `<svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="9" cy="9" rx="5" ry="5" fill="#94a3b8"/>
    <ellipse cx="23" cy="9" rx="5" ry="5" fill="#94a3b8"/>
    <ellipse cx="9" cy="9" rx="3" ry="3" fill="#e2e8f0"/>
    <ellipse cx="23" cy="9" rx="3" ry="3" fill="#e2e8f0"/>
    <ellipse cx="16" cy="18" rx="11" ry="10" fill="#e2e8f0"/>
    <ellipse cx="16" cy="24" rx="4" ry="5" fill="#cbd5e1"/>
    <ellipse cx="16" cy="22" rx="1.8" ry="1.2" fill="#f9a8d4"/>
    <ellipse cx="12" cy="16" rx="1.8" ry="1.8" fill="#1e293b"/>
    <ellipse cx="20" cy="16" rx="1.8" ry="1.8" fill="#1e293b"/>
    <ellipse cx="12.6" cy="15.4" rx="0.5" ry="0.5" fill="#f8fafc"/>
    <ellipse cx="20.6" cy="15.4" rx="0.5" ry="0.5" fill="#f8fafc"/>
  </svg>`,
  groundhog: `<svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="10" cy="10" rx="4" ry="4" fill="#92400e"/>
    <ellipse cx="22" cy="10" rx="4" ry="4" fill="#92400e"/>
    <ellipse cx="10" cy="10" rx="2.5" ry="2.5" fill="#b45309"/>
    <ellipse cx="22" cy="10" rx="2.5" ry="2.5" fill="#b45309"/>
    <ellipse cx="16" cy="18" rx="12" ry="11" fill="#92400e"/>
    <ellipse cx="9" cy="20" rx="4" ry="3.5" fill="#b45309"/>
    <ellipse cx="23" cy="20" rx="4" ry="3.5" fill="#b45309"/>
    <ellipse cx="16" cy="22" rx="4" ry="3" fill="#b45309"/>
    <ellipse cx="16" cy="20.5" rx="1.5" ry="1" fill="#1c1917"/>
    <ellipse cx="12" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="20" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="12" cy="16" rx="1.2" ry="1.2" fill="#78350f"/>
    <ellipse cx="20" cy="16" rx="1.2" ry="1.2" fill="#78350f"/>
    <rect x="14.5" y="23.5" width="1.5" height="2" rx="0.4" fill="#fef9c3"/>
    <rect x="16.5" y="23.5" width="1.5" height="2" rx="0.4" fill="#fef9c3"/>
  </svg>`,
  fox: `<svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <polygon points="9,15 4,1 14,10" fill="#ea580c"/>
    <polygon points="23,15 28,1 18,10" fill="#ea580c"/>
    <polygon points="9,13 6,4 13,10" fill="#fef3c7"/>
    <polygon points="23,13 26,4 19,10" fill="#fef3c7"/>
    <ellipse cx="16" cy="18" rx="11" ry="10" fill="#ea580c"/>
    <ellipse cx="16" cy="20" rx="6" ry="7" fill="#fef3c7"/>
    <ellipse cx="16" cy="24" rx="4" ry="3.5" fill="#fef3c7"/>
    <ellipse cx="16" cy="22.5" rx="1.8" ry="1.2" fill="#1c1917"/>
    <ellipse cx="11.5" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="20.5" cy="16" rx="2" ry="2" fill="#fef3c7"/>
    <ellipse cx="11.5" cy="16" rx="1.2" ry="1.2" fill="#c2410c"/>
    <ellipse cx="20.5" cy="16" rx="1.2" ry="1.2" fill="#c2410c"/>
    <ellipse cx="11.9" cy="15.6" rx="0.4" ry="0.4" fill="#fff"/>
    <ellipse cx="20.9" cy="15.6" rx="0.4" ry="0.4" fill="#fff"/>
  </svg>`,
};

function makeAnimalIcon(animalId) {
  const svg = ANIMAL_SVG_HTML[animalId] || ANIMAL_SVG_HTML.raccoon;
  return window.L.divIcon({
    html: `<div style="width:28px;height:28px;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4))">${svg}</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
}

const TORONTO_BOUNDS = { swlat: 43.58, swlng: -79.64, nelat: 43.86, nelng: -79.12 };
const TORONTO_CENTER = [43.720, -79.380];
const DATE_OPTIONS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1yr", days: 365 },
  { label: "All", days: null },
];

// ─── Data ─────────────────────────────────────────────────────────────────────
async function fetchAnimal(animal, days) {
  const params = new URLSearchParams({
    taxon_id: animal.taxonId,
    swlat: TORONTO_BOUNDS.swlat,
    swlng: TORONTO_BOUNDS.swlng,
    nelat: TORONTO_BOUNDS.nelat,
    nelng: TORONTO_BOUNDS.nelng,
    quality_grade: "research",
    per_page: 200,
    order: "desc",
    order_by: "observed_on",
  });
  if (days) {
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    params.set("d1", since);
  }
  const res = await fetch(`https://api.inaturalist.org/v1/observations?${params}`);
  const json = await res.json();
  return (json.results || []).map(obs => {
    const [lat, lng] = (obs.location || "").split(",").map(Number);
    return {
      id: obs.id,
      animalId: animal.id,
      animalLabel: animal.label,
      animalColor: animal.color,
      date: obs.observed_on,
      place: obs.place_guess || "Toronto",
      lat: isNaN(lat) ? null : lat,
      lng: isNaN(lng) ? null : lng,
      photo: obs.photos?.[0]?.url?.replace("square", "small") || null,
      user: obs.user?.login || "unknown",
      url: `https://www.inaturalist.org/observations/${obs.id}`,
    };
  });
}

// ─── Map ──────────────────────────────────────────────────────────────────────
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { animate: true, duration: 0.6 });
  }, [center, zoom, map]);
  return null;
}

function AnimalMap({ observations, colorMode, border }) {
  const dark = colorMode === "dark";
  const tileUrl = dark
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
  const markers = observations.filter(o => o.lat && o.lng);

  return (
    <MapContainer center={TORONTO_CENTER} zoom={11} style={{ width: "100%", height: "100%" }}>
      <TileLayer url={tileUrl} attribution='&copy; <a href="https://carto.com/">CARTO</a>' />
      {dark && (
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png"
          opacity={0.85}
          pane="shadowPane"
        />
      )}
      <MapController center={TORONTO_CENTER} zoom={11} />
      {markers.map(obs => (
        <Marker
          key={`${obs.animalId}-${obs.id}`}
          position={[obs.lat, obs.lng]}
          icon={makeAnimalIcon(obs.animalId)}
        >
          <Popup autoPan autoPanPadding={[20, 20]} minWidth={200}>
            <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
              {obs.photo && (
                <img src={obs.photo} alt={obs.animalLabel} style={{ width: "100%", borderRadius: 6, marginBottom: 6, display: "block" }} />
              )}
              <div style={{ fontWeight: 700, fontSize: "0.88rem", color: obs.animalColor, marginBottom: 3 }}>
                {obs.animalLabel} sighting
              </div>
              <div style={{ fontSize: "0.75rem", color: "#475569", marginBottom: 2 }}>📍 {obs.place}</div>
              <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginBottom: 6 }}>
                📅 {obs.date} · by {obs.user}
              </div>
              <a href={obs.url} target="_blank" rel="noreferrer"
                style={{ fontSize: "0.72rem", color: obs.animalColor, fontWeight: 600, textDecoration: "none" }}>
                View on iNaturalist ↗
              </a>
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Map legend */}
      <div style={{ position: "absolute", bottom: 16, right: 10, zIndex: 1000,
        background: dark ? "rgba(17,24,39,0.93)" : "rgba(255,255,255,0.93)",
        backdropFilter: "blur(8px)", border: `1px solid ${border}`,
        borderRadius: 8, padding: "8px 12px", pointerEvents: "none" }}>
        <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", marginBottom: 5 }}>Legend</div>
        {ANIMALS.map(a => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <div style={{ width: 20, height: 20, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: ANIMAL_SVG_HTML[a.id].replace('width="28" height="28"', 'width="20" height="20"') }} />
            <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>{a.label}</span>
          </div>
        ))}
      </div>
    </MapContainer>
  );
}

// ─── List card ────────────────────────────────────────────────────────────────
function SightingCard({ obs, dark, index }) {
  const animal = ANIMALS.find(a => a.id === obs.animalId);
  const Icon = animal?.Icon;
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      px: 2, py: 1.25,
      borderBottom: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
      borderLeft: `3px solid ${obs.animalColor}`,
      bgcolor: dark
        ? (index % 2 === 0 ? "transparent" : "#ffffff08")
        : (index % 2 === 0 ? "#ffffff" : "#f8fafc"),
    }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 1, overflow: "hidden", flexShrink: 0, bgcolor: dark ? "#1e293b" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {obs.photo
          ? <img src={obs.photo} alt={obs.animalLabel} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : Icon ? <Icon size={28} /> : null}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: obs.animalColor }}>
            {obs.animalLabel}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            · {obs.place}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {obs.date} · {obs.user}
        </Typography>
      </Box>
      <Tooltip title="View on iNaturalist">
        <IconButton size="small" component="a" href={obs.url} target="_blank" rel="noreferrer" sx={{ color: obs.animalColor, flexShrink: 0 }}>
          <OpenInNewIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── AnimalPage ───────────────────────────────────────────────────────────────
export default function RaccoonPage({ colorMode, setColorMode, dark, border, barBg, isMobile }) {
  const [viewMode, setViewMode] = useState("map");
  const [dateFilter, setDateFilter] = useState(30);
  const [activeAnimal, setActiveAnimal] = useState(null); // null = all
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(ANIMALS.map(a => fetchAnimal(a, dateFilter)));
      const all = results.flat().sort((a, b) => (b.date > a.date ? 1 : -1));
      setObservations(all);
      setLastRefresh(new Date());
    } catch {
      setError("Failed to load sighting data. Try refreshing.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFilter]);

  useEffect(() => { load(); }, [load]);

  const displayed = activeAnimal ? observations.filter(o => o.animalId === activeAnimal) : observations;
  const withCoords = displayed.filter(o => o.lat && o.lng);
  const activeFilterCount = [dateFilter !== 30, activeAnimal !== null].filter(Boolean).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Header ── */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "text.primary" }}>
          Animal Sightings
        </Typography>
        {!isMobile && (
          <Chip label="iNaturalist · Research grade" size="small"
            sx={{ fontSize: "0.65rem", height: 18, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600, ml: 0.5 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {displayed.length > 0 && (
          <Chip label={`${displayed.length} sightings`} size="small"
            sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 700 }} />
        )}
        {lastRefresh && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, fontSize: "0.72rem" }}>
            {lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(true)} disabled={refreshing} sx={{ color: "text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 17 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDrawerOpen(true)} sx={{ color: activeFilterCount > 0 ? "#3b82f6" : "text.secondary" }}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <TuneIcon sx={{ fontSize: 17 }} />
              {activeFilterCount > 0 && (
                <Box sx={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, borderRadius: "50%", bgcolor: "#3b82f6", fontSize: "0.5rem", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Filter Drawer ── */}
      <SwipeableDrawer
        anchor={isMobile ? "bottom" : "right"}
        open={filterDrawerOpen}
        onOpen={() => setFilterDrawerOpen(true)}
        onClose={() => setFilterDrawerOpen(false)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            bgcolor: barBg,
            borderTop: isMobile ? `1px solid ${border}` : "none",
            borderLeft: !isMobile ? `1px solid ${border}` : "none",
            borderRadius: isMobile ? "12px 12px 0 0" : 0,
            width: isMobile ? "100%" : 280,
            maxHeight: isMobile ? "80vh" : "100vh",
          }
        }}
      >
        <Box sx={{ px: 2.5, pt: isMobile ? 2 : 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${border}` }}>
          {isMobile && <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: border, position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)" }} />}
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Filters</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "text.secondary", mr: -0.5 }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto" }}>

          {/* View */}
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>View</Typography>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" fullWidth>
              <ToggleButton value="list" sx={{ flex: 1 }}><ListIcon sx={{ fontSize: 14, mr: 0.5 }} />List</ToggleButton>
              <ToggleButton value="map" sx={{ flex: 1 }}><MapIcon sx={{ fontSize: 14, mr: 0.5 }} />Map</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Animal filter */}
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Animal</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box onClick={() => setActiveAnimal(null)} sx={{
                display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 0.75, borderRadius: 1, cursor: "pointer",
                bgcolor: activeAnimal === null ? (dark ? "#1e293b" : "#f1f5f9") : "transparent",
                border: `1px solid ${activeAnimal === null ? "#3b82f6" : "transparent"}`,
              }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: activeAnimal === null ? 700 : 400, color: activeAnimal === null ? "text.primary" : "text.secondary" }}>All animals</Typography>
                <Chip label={observations.length} size="small" sx={{ ml: "auto", height: 18, fontSize: "0.65rem", bgcolor: dark ? "#0f172a" : "#e2e8f0", color: "text.secondary" }} />
              </Box>
              {ANIMALS.map(a => {
                const count = observations.filter(o => o.animalId === a.id).length;
                const Icon = a.Icon;
                return (
                  <Box key={a.id} onClick={() => setActiveAnimal(activeAnimal === a.id ? null : a.id)} sx={{
                    display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 0.75, borderRadius: 1, cursor: "pointer",
                    bgcolor: activeAnimal === a.id ? (dark ? "#1e293b" : "#f1f5f9") : "transparent",
                    border: `1px solid ${activeAnimal === a.id ? a.color : "transparent"}`,
                  }}>
                    <Icon size={20} />
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: activeAnimal === a.id ? 700 : 400, color: activeAnimal === a.id ? a.color : "text.secondary", flex: 1 }}>{a.label}</Typography>
                    <Chip label={count} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: dark ? "#0f172a" : "#e2e8f0", color: "text.secondary" }} />
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Date range */}
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Date range</Typography>
            <ToggleButtonGroup value={dateFilter} exclusive size="small" fullWidth
              onChange={(_, v) => { if (v !== null) { setDateFilter(v); setFilterDrawerOpen(false); } }}>
              {DATE_OPTIONS.map(o => (
                <ToggleButton key={o.label} value={o.days} sx={{ flex: 1 }}>{o.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Appearance */}
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Appearance</Typography>
            <ToggleButtonGroup value={colorMode} exclusive onChange={(_, v) => v && setColorMode(v)} size="small" fullWidth>
              <ToggleButton value="light" sx={{ flex: 1 }}>Light</ToggleButton>
              <ToggleButton value="dark" sx={{ flex: 1 }}>Dark</ToggleButton>
            </ToggleButtonGroup>
          </Box>

        </Box>
      </SwipeableDrawer>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: "0.82rem" }}>{error}</Alert>}

      {/* ── Content ── */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", gap: 1 }}>
            <CircularProgress size={30} sx={{ color: "#a855f7" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Loading sightings for all animals…</Typography>
          </Box>
        ) : viewMode === "list" ? (
          <Box sx={{ height: "100%", overflow: "auto", bgcolor: "background.default" }}>
            {displayed.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No sightings found for this period.</Typography>
              </Box>
            ) : (
              displayed.map((obs, idx) => (
                <SightingCard key={`${obs.animalId}-${obs.id}`} obs={obs} dark={dark} index={idx} />
              ))
            )}
          </Box>
        ) : (
          <Box sx={{ height: "100%", position: "relative" }}>
            <AnimalMap observations={displayed} colorMode={colorMode} border={border} />
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          Data: iNaturalist · Research-grade observations only
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          {withCoords.length} mappable · {displayed.length} total
        </Typography>
      </Box>
    </Box>
  );
}
